import { FC, useCallback, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataAccessProvider, SelfServeDataAccessStatus } from '@constants/constants';
import { useAdminUpdateSelfServeMutation } from '@api/selfServe';
import appsflyerLogo from '../../../../../../assets/icons/appsflyer-logo.png';
import adjustLogo from '../../../../../../assets/icons/adjust-logo.png';
import bigQueryLogo from '../../../../../../assets/icons/big-query-logo.png';
import gcsLogo from '../../../../../../assets/icons/gcs-logo.png';
import s3Logo from '../../../../../../assets/icons/s3-logo.png';
import mySqlLogo from '../../../../../../assets/icons/mysql-logo.png';
import snowFlakeLogo from '../../../../../../assets/icons/snowflake-logo.png';
import googleDriveLogo from '../../../../../../assets/icons/google-drive-logo.png';
import aggregatedCohortDataLogo from '../../../../../../assets/icons/aggregated-cohort-data-icon.png';
import DataAccessSourceFields from '../../../components/DataAccessSourceFields';

export interface DataSourceDetails {
  accountName: string | null;
  iapMargin: string | null;
  iaaIdList: string[] | null;
  connectionDetails: any;
  refreshRequired?: boolean;
}

interface DataSourceCardProps {
  requestId: string | null;
  requestStatus: SelfServeDataAccessStatus | null;
  mmp: DataAccessProvider | null;
  newGameData: DataSourceDetails;
}

const RERUN_DATA_SOURCES = [
  DataAccessProvider.APPS_FLYER,
  DataAccessProvider.ADJUST,
  // DataAccessProvider.AGGREGATED_DATA,
];

const DataSourceCard: FC<DataSourceCardProps> = ({
  requestId,
  requestStatus,
  mmp,
  newGameData,
}) => {
  const [selectedGameData, setSelectedGameData] = useState<DataSourceDetails>(newGameData);
  const [isEditDataSource, setIsEditDataSource] = useState(false);
  const [adminUpdateSelfServeMutation, { isLoading: isUpdateLoading }] =
    useAdminUpdateSelfServeMutation();

  const getProviderDetails = useCallback(() => {
    switch (mmp) {
      case DataAccessProvider.APPS_FLYER:
        return { iconUrl: appsflyerLogo, label: 'AppsFlyer' };
      case DataAccessProvider.ADJUST:
        return { iconUrl: adjustLogo, label: 'Adjust' };
      case DataAccessProvider.BIGQUERY:
        return { iconUrl: bigQueryLogo, label: 'BigQuery' };
      case DataAccessProvider.GCS:
        return { iconUrl: gcsLogo, label: 'Google Cloud Storage' };
      case DataAccessProvider.S3:
        return { iconUrl: s3Logo, label: 'Amazon S3' };
      case DataAccessProvider.MYSQL:
        return { iconUrl: mySqlLogo, label: 'My SQL' };
      case DataAccessProvider.SNOWFLAKE:
        return { iconUrl: snowFlakeLogo, label: 'Snowflake' };
      case DataAccessProvider.GOOGLE_DRIVE:
        return {
          iconUrl: googleDriveLogo,
          label: 'Direct File Share (Cloud Storage Link)',
        };
      case DataAccessProvider.NONE:
        return { iconUrl: undefined, label: 'Other' };
      case DataAccessProvider.AGGREGATED_DATA:
        return {
          iconUrl: aggregatedCohortDataLogo,
          label: 'Aggregated Cohort Data',
        };
      default:
        return { iconUrl: undefined, label: 'Other' };
    }
  }, [mmp]);

  const handleUpdateNewGameData = (key: string, value: string) => {
    setSelectedGameData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveClick = async () => {
    if (!requestId) return;
    await adminUpdateSelfServeMutation({
      selfServeId: requestId,
      gameData: {
        ...selectedGameData,
        mmp: mmp,
        refreshRequired: Boolean(selectedGameData.refreshRequired),
      },
    });
    setIsEditDataSource(false);
  };

  return (
    <>
      {mmp !== DataAccessProvider.NONE && (
        <Box
          sx={{
            background: '#fff',
            border: '1px solid #2F736E1F',
            borderRadius: '1rem',
            height: 'auto',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem 1rem',
            gap: '1rem',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              background: 'transparent',
              gap: '0.25rem',
              color: '#6C6C6C',
              fontSize: '0.875rem',
              fontWeight: 700,
              lineHeight: '1.25rem',
              letterSpacing: '-0.35px',
              border: '1px solid #2F736E1F',
              borderRadius: '0.5rem',
              padding: '0.25rem 0.5rem',
            }}
          >
            {getProviderDetails().iconUrl && (
              <img height={'100%'} src={getProviderDetails().iconUrl || ''} alt={`logo-${mmp}`} />
            )}
            {getProviderDetails().label}
          </Box>
          <Box sx={{ width: '100%' }}>
            <DataAccessSourceFields
              dataAccessSource={mmp}
              newGameData={selectedGameData}
              handleUpdateNewGameData={handleUpdateNewGameData}
              disabled={isUpdateLoading || !isEditDataSource}
            />
          </Box>
          {/* TODO: UNDO COMMENT */}
          {/* <Button
            disableElevation
            disableFocusRipple
            disableRipple
            disableTouchRipple
            disabled={Boolean(
              isUpdateLoading ||
                (RERUN_DATA_SOURCES.includes(mmp as DataAccessProvider) &&
                  requestStatus &&
                  ![
                    SelfServeDataAccessStatus.DATA_FETCH_FAILED,
                    SelfServeDataAccessStatus.PUBLISHED,
                  ].includes(requestStatus)),
            )}
            onClick={() => {
              if (isEditDataSource) {
                handleSaveClick();
              } else {
                setIsEditDataSource(true);
              }
            }}
            variant="contained"
            color="primary"
            sx={{
              width: '8rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '1.25rem',
              textTransform: 'none',
              background: '#2F736E',
              color: '#fff',
            }}
          >
            {!isEditDataSource ? (
              <Typography fontSize={'1rem'} fontWeight={500} sx={{ color: '#FOFOF1' }}>
                Edit
              </Typography>
            ) : (
              <Typography fontSize={'1rem'} fontWeight={500} sx={{ color: '#FOFOF1' }}>
                {RERUN_DATA_SOURCES.includes(mmp as DataAccessProvider) ? 'Re-run' : 'Save'}
              </Typography>
            )}
            {isUpdateLoading ? (
              <CircularProgress
                size={20}
                sx={{
                  marginLeft: '0.5rem',
                  color: '#F0F0F1',
                }}
              />
            ) : (
              <></>
            )}
          </Button> */}
        </Box>
      )}
    </>
  );
};

export default DataSourceCard;
